import { useState, useCallback } from 'react';
import { formatDate, localDayKey } from '@/utils/dates';
import { supabase } from '../lib/supabase';
import type { RankedPlace } from './useLLM';
import type { TripGrouping } from './useSettings';
import { placePhotoUrl } from '../utils/googleProxy';
import { recordTasteEvent } from '../lib/tasteGraph';
import { reportError } from '@/lib/reportError';
import type { ReevaluatedPlace, ReevaluateContext } from '@/services/reevaluate';

export interface SavedItinerary {
  id: string;
  title: string;
  location: string;
  time_available: string;
  transport: string;
  vibes: string[];
  ai_mode: string;
  /** What actually ranked the trip: 'ai' or 'offline'. ai_mode is what was asked for. */
  ranking_source?: 'ai' | 'offline' | null;
  ranking_model?: string | null;
  created_at: string;
  places: SavedPlace[];
  share_slug?: string | null;
  is_shared?: boolean;
  share_language?: string | null;
  /** H-213: pinned trip. Up to TWO pinned per user; new pins displace the
   *  most-recent (FIFO on slot 2). First-pinned (oldest pinned_at) is sticky. */
  is_pinned?: boolean;
  pinned_at?: string | null;
}

export interface SavedPlace {
  id: string;
  place_id: string;
  name: string;
  address: string | null;
  rating: number | null;
  user_ratings_total: number;
  types: string[];
  photo_url: string | null;
  match_reason: string | null;
  match_score: number;
  sort_order: number;
  hook: string | null;
  estimated_duration: string | null;
  category: string | null;
  distance_note: string | null;
  matching_vibes: string[];
  formatted_address: string | null;
  location_lat: number | null;
  location_lng: number | null;
  website_uri: string | null;
  opening_hours: {
    openNow?: boolean;
    weekdayDescriptions?: string[];
  } | null;
}

export interface GroupedItineraries {
  groupKey: string;
  groupLabel: string;
  itineraries: SavedItinerary[];
}

export const MAX_FREE_DAY_GROUPS = 5;
export const MAX_FREE_LOCATION_GROUPS = 3;
export const FREE_ARCHIVE_DAYS = 30;

/** Returns true if a trip should be archived for free users */
export function isItineraryArchived(createdAt: string, isPremium: boolean): boolean {
  if (isPremium) return false;
  const created = new Date(createdAt);
  const now = new Date();
  const diffDays = (now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24);
  return diffDays >= FREE_ARCHIVE_DAYS;
}

/** Returns days until a trip gets archived (null if premium or already archived) */
export function daysUntilArchive(createdAt: string, isPremium: boolean): number | null {
  if (isPremium) return null;
  const created = new Date(createdAt);
  const now = new Date();
  const diffDays = (now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24);
  const remaining = FREE_ARCHIVE_DAYS - diffDays;
  if (remaining <= 0) return 0;
  return Math.ceil(remaining);
}

// Hybrid archive gate — MUST stay in lockstep with tripboom-website/src/pages/Trips.tsx
// helpers `isTripArchivedHybrid` / `isGroupArchived`. A trip archives for free users
// when EITHER its age >= FREE_ARCHIVE_DAYS OR its group is beyond the free limit.

/** True if a specific trip is archived under the hybrid rule (age OR position). */
export function isTripArchivedHybrid(
  createdAt: string,
  groupIndex: number,
  isPremium: boolean,
  freeLimit: number,
): boolean {
  if (isPremium) return false;
  if (groupIndex >= freeLimit) return true;
  return isItineraryArchived(createdAt, false);
}

/** True if every trip in the group is archived (group-level lock). */
export function isGroupArchived(
  itineraries: { created_at: string }[],
  groupIndex: number,
  isPremium: boolean,
  freeLimit: number,
): boolean {
  if (isPremium) return false;
  if (groupIndex >= freeLimit) return true;
  if (itineraries.length === 0) return false;
  return itineraries.every(i => isItineraryArchived(i.created_at, false));
}

/**
 * Determine a merged title when combining trips.
 * H-217 Rule 2 (most-POIs-first): order by placeCount DESC for both the
 * 2-renamed concat and the 3+ pick. Ties preserve selection order
 * (Array.sort is stable in V8).
 *
 * Rules:
 *  1. One renamed + unrenamed → keep the renamed name
 *  2. Two renamed → concatenate "<larger> — <smaller>" by place count
 *  3. Three+ renamed → keep the name of the one with most places
 *  4. All unrenamed → fall through to generateTitle
 *
 * NOTE: H-217 Rule 1 (pin precedence) is handled INSIDE mergeItineraries —
 * if any source is pinned, the merged title is that pinned trip's verbatim
 * title and getMergedTitle is bypassed entirely.
 */
function getMergedTitle(
  renamedTitles: { title: string; placeCount: number }[]
): string | null {
  if (renamedTitles.length === 0) return null; // all unrenamed
  const sorted = [...renamedTitles].sort((a, b) => b.placeCount - a.placeCount);
  if (sorted.length === 1) return sorted[0].title;
  if (sorted.length === 2) return `${sorted[0].title} — ${sorted[1].title}`;
  return sorted[0].title;
}

export function generateTitle(location: string, vibes: string[], places: RankedPlace[], sourceLocations?: string[], timeAvailable?: string, transport?: string): string {
  const vibeLabels: Record<string, string> = {
    hungry: 'Food',
    adventurous: 'Adventures',
    'must-sees': 'Must-Sees',
    'hidden-gems': 'Hidden Gems',
    popular: 'Popular',
    'feeling-lucky': 'Discoveries'
  };

  // For merges with multiple source locations, extract unique city names from each
  let cityName: string;
  if (sourceLocations && sourceLocations.length > 1) {
    const uniqueCities = [...new Set(sourceLocations.map(loc => extractCityBucket(loc)))];
    cityName = uniqueCities.slice(0, 3).join(' & ');
  } else {
    cityName = extractCityBucket(location);
  }

  const customVibes = vibes.filter(v => v.startsWith('custom:'));
  const standardVibes = vibes.filter(v => !v.startsWith('custom:'));

  const customVibeLabels = customVibes.map(v => v.slice(7));

  // Build a context suffix for variety (e.g. "on Foot", "Evening", "Morning")
  const timeLabels: Record<string, string> = {
    '1-2-hrs': 'Quick',
    'half-day': 'Half-Day',
    'full-day': 'Full Day',
    '2-3-days': 'Multi-Day'
  };
  const transportLabels: Record<string, string> = {
    walking: 'on Foot',
    transit: 'by Transit',
    taxi: 'by Car'
  };
  const currentHour = new Date().getHours();
  const timeOfDay = currentHour < 12 ? 'Morning' : currentHour < 17 ? 'Afternoon' : 'Evening';

  // Single place: use place name
  if (places.length === 1) {
    const place = places[0];
    if (customVibeLabels.length > 0) {
      return `${place.name} — ${customVibeLabels[0]} in ${cityName}`;
    }
    const matchedVibes = place.matchingVibes
      ? (Array.isArray(place.matchingVibes) ? place.matchingVibes : [place.matchingVibes]).filter(v => !v.startsWith('custom:'))
      : [];
    const vibeLabel = matchedVibes.length > 0
      ? vibeLabels[matchedVibes[0]] || matchedVibes[0]
      : standardVibes.length > 0
        ? vibeLabels[standardVibes[0]] || standardVibes[0]
        : null;
    if (vibeLabel) {
      return `${place.name} — ${vibeLabel}`;
    }
    return `${place.name}, ${cityName}`;
  }

  // Custom-only vibe (e.g. "Scuba")
  if (customVibeLabels.length > 0 && standardVibes.length === 0) {
    const label = customVibeLabels.slice(0, 2).join(' & ');
    if (transport && transportLabels[transport]) {
      return `${label} ${transportLabels[transport]} in ${cityName}`;
    }
    return `${label} in ${cityName}`;
  }

  // Mixed: standard + custom vibes
  if (customVibeLabels.length > 0) {
    const stdLabel = standardVibes.slice(0, 1).map(v => vibeLabels[v] || v).join('');
    return `${stdLabel} & ${customVibeLabels[0]} in ${cityName}`;
  }

  // Standard vibes only — add context for variety
  const vibeText = standardVibes.length > 0
    ? standardVibes.map(v => vibeLabels[v] || v).slice(0, 2).join(' & ')
    : 'Places';

  // Add differentiating context when we have it
  if (timeAvailable && transport) {
    const timeLbl = timeLabels[timeAvailable];
    const transportLbl = transportLabels[transport];
    if (timeLbl && transportLbl) {
      return `${timeLbl} ${vibeText} ${transportLbl} — ${cityName}`;
    }
    if (transportLbl) {
      return `${vibeText} ${transportLbl} in ${cityName}`;
    }
  }
  if (transport && transportLabels[transport]) {
    return `${vibeText} ${transportLabels[transport]} in ${cityName}`;
  }
  // Add time-of-day for variety when no transport info
  if (timeAvailable && timeLabels[timeAvailable]) {
    return `${timeOfDay} ${vibeText} in ${cityName}`;
  }

  return `${vibeText} in ${cityName}`;
}

function formatDateLabel(dateStr: string): string {
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const dateKey = localDayKey(dateStr);
  const todayKey = localDayKey(today);
  const yesterdayKey = localDayKey(yesterday);

  if (dateKey === todayKey) return 'Today';
  if (dateKey === yesterdayKey) return 'Yesterday';

  return formatDate(date, { weekday: true });
}

function extractCityBucket(location: string): string {
  const parts = location.split(',').map(p => p.trim());
  if (parts.length >= 3) {
    return parts[1];
  }
  return parts[0];
}

function normalizeLocation(location: string): string {
  return extractCityBucket(location).toLowerCase();
}

function formatLocationLabel(location: string): string {
  return extractCityBucket(location);
}

export type SharedTripResult =
  | { status: 'found'; itinerary: SavedItinerary }
  | { status: 'not_found' }
  | { status: 'error' };

/** Fetch a shared trip by slug, readable signed out or signed in (#166).
 *  Also checks share_redirects table so merged-trip links still work.
 *  'error' means a query failed, so the trip may exist: the page offers a retry. */
export async function getSharedTrip(slug: string): Promise<SharedTripResult> {
  if (!supabase) return { status: 'error' };

  const failed = (step: string, err: unknown): SharedTripResult => {
    reportError('sharedTrip.load', err, { slug, step });
    return { status: 'error' };
  };

  // 1. Direct slug match on the itinerary
  const { data: direct, error: directError } = await supabase
    .from('itineraries')
    .select('*')
    .eq('share_slug', slug)
    .eq('is_shared', true)
    .maybeSingle();
  if (directError) return failed('itinerary', directError);
  let itin = direct;

  // 2. Fallback: check share_redirects (handles merged sources)
  if (!itin) {
    const { data: redirect, error: redirectError } = await supabase
      .from('share_redirects')
      .select('target_itinerary_id')
      .eq('slug', slug)
      .maybeSingle();
    if (redirectError) return failed('redirect', redirectError);

    if (redirect) {
      const { data: redirectedItin, error: redirectedError } = await supabase
        .from('itineraries')
        .select('*')
        .eq('id', redirect.target_itinerary_id)
        .eq('is_shared', true)
        .maybeSingle();
      if (redirectedError) return failed('redirectedItinerary', redirectedError);

      itin = redirectedItin;
    }
  }

  if (!itin) return { status: 'not_found' };

  const { data: places, error: placesError } = await supabase
    .from('saved_places')
    .select('*')
    .eq('itinerary_id', itin.id)
    .order('sort_order', { ascending: true });
  if (placesError) return failed('places', placesError);

  return {
    status: 'found',
    itinerary: {
      ...itin,
      places: places || []
    }
  };
}

/**
 * H-215: save a trip that was reevaluated by an anonymous viewer of a
 * shared link. Called from App.tsx's post-auth materialiser after the user
 * signs up / signs in via the AuthModal opened from SharedTripPage.
 *
 * Inserts a fresh itinerary owned by `userId` with the reevaluated places
 * (using ReevaluatedPlace.sort_order + .hook + .distanceNote, falling back
 * to the source SavedPlace fields for everything else). is_shared=false,
 * no share_slug — this is the new owner's private copy.
 *
 * Conditional auto-pin (per Ian session 11): only auto-pin if the new owner
 * has 0 existing pins. Otherwise the trip is saved unpinned and the caller
 * should toast the user about it. Returns { itinerary, autoPinned }.
 */
export async function saveReevaluatedTrip(
  userId: string,
  shareSlug: string,
  context: ReevaluateContext,
  reevaluated: ReevaluatedPlace[],
): Promise<{ itinerary: SavedItinerary; autoPinned: boolean } | null> {
  if (!supabase) return null;

  // Build a RankedPlace[]-ish view from ReevaluatedPlace + source SavedPlace
  // for generateTitle (which only reads name + matchingVibes). Required Place
  // fields (location, formattedAddress) are filled with safe defaults; the
  // helper doesn't read them.
  const titleInput: RankedPlace[] = reevaluated.map((r) => ({
    id: r.source.place_id,
    name: r.source.name,
    types: r.source.types,
    rating: r.source.rating ?? undefined,
    userRatingCount: r.source.user_ratings_total,
    location: {
      lat: r.source.location_lat ?? 0,
      lng: r.source.location_lng ?? 0,
    },
    formattedAddress: r.source.formatted_address ?? '',
    websiteUri: r.source.website_uri ?? undefined,
    hook: r.hook,
    estimatedDuration: r.source.estimated_duration ?? '',
    category: r.source.category ?? '',
    distanceNote: r.distanceNote,
    matchingVibes: r.source.matching_vibes,
    source: r.rankingSource,
  }));

  const titleVibes = Array.from(new Set(reevaluated.flatMap((r) => r.source.matching_vibes)));
  const title = generateTitle(
    context.locationLabel,
    titleVibes,
    titleInput,
    undefined,
    context.timeAvailable,
    context.transport,
  );

  const { data: itinerary, error: itineraryError } = await supabase
    .from('itineraries')
    .insert({
      user_id: userId,
      title,
      location: context.locationLabel,
      time_available: context.timeAvailable,
      transport: context.transport,
      vibes: titleVibes,
      ai_mode: 'low',
      ranking_source: reevaluated.some((r) => r.rankingSource === 'offline') ? 'offline' : 'ai',
      ranking_model: reevaluated.find((r) => r.model)?.model ?? null,
    })
    .select()
    .single();

  if (itineraryError || !itinerary) {
    console.error('[saveReevaluatedTrip] insert itinerary failed:', itineraryError);
    return null;
  }

  const placesToInsert = reevaluated.map((r) => ({
    itinerary_id: itinerary.id,
    place_id: r.source.place_id,
    name: r.source.name,
    address: r.source.address,
    rating: r.source.rating,
    user_ratings_total: r.source.user_ratings_total,
    types: r.source.types,
    photo_url: r.source.photo_url,
    match_reason: r.source.match_reason,
    match_score: r.source.match_score,
    sort_order: r.sort_order,
    hook: r.hook,
    estimated_duration: r.source.estimated_duration,
    category: r.source.category,
    distance_note: r.distanceNote,
    matching_vibes: r.source.matching_vibes,
    formatted_address: r.source.formatted_address,
    location_lat: r.source.location_lat,
    location_lng: r.source.location_lng,
    website_uri: r.source.website_uri,
    opening_hours: r.source.opening_hours,
  }));

  const { error: placesError } = await supabase
    .from('saved_places')
    .insert(placesToInsert);

  if (placesError) {
    console.error('[saveReevaluatedTrip] insert saved_places failed:', placesError);
    return null;
  }

  // Conditional auto-pin: only if the user has 0 existing pinned trips.
  let autoPinned = false;
  const { count: pinCount, error: countError } = await supabase
    .from('itineraries')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('is_pinned', true);

  if (countError) {
    console.error('[saveReevaluatedTrip] pin-count query failed:', countError);
  } else if ((pinCount ?? 0) === 0) {
    const { data: pinResult, error: pinError } = await supabase.rpc(
      'set_pinned_itinerary',
      { p_id: itinerary.id },
    );
    if (pinError) {
      console.error('[saveReevaluatedTrip] auto-pin RPC failed:', pinError);
    } else {
      autoPinned = Boolean(pinResult);
    }
  }
  // shareSlug is currently unused in the insert (no provenance column);
  // referenced here so linters don't flag the parameter and so future
  // schema work can attribute the new trip to its source share.
  void shareSlug;

  return {
    itinerary: { ...itinerary, places: [], is_pinned: autoPinned } as SavedItinerary,
    autoPinned,
  };
}

export function useItineraries() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const saveItinerary = useCallback(async (
    userId: string,
    location: string,
    timeAvailable: string,
    transport: string,
    vibes: string[],
    places: RankedPlace[],
    aiMode: string = 'low',
    country?: string
  ): Promise<SavedItinerary | null> => {
    setLoading(true);
    setError(null);

    try {
      if (!supabase) {
        setError('Database not configured');
        return null;
      }
      const title = generateTitle(location, vibes, places, undefined, timeAvailable, transport);

      const { data: itinerary, error: itineraryError } = await supabase
        .from('itineraries')
        .insert({
          user_id: userId,
          title,
          location,
          time_available: timeAvailable,
          transport,
          vibes,
          ai_mode: aiMode,
          ranking_source: places.some(p => p.source === 'offline') ? 'offline' : 'ai',
          ranking_model: places.find(p => p.model)?.model ?? null,
          ...(country ? { country } : {})
        })
        .select()
        .single();

      if (itineraryError) {
        setError(itineraryError.message);
        return null;
      }

      const placesToInsert = places.map((place, index) => {
        const vibes = place.matchingVibes
          ? (Array.isArray(place.matchingVibes) ? place.matchingVibes : [place.matchingVibes])
          : [];
        return {
          itinerary_id: itinerary.id,
          place_id: place.id,
          name: place.name,
          address: place.formattedAddress || null,
          rating: place.rating || null,
          user_ratings_total: place.userRatingCount || 0,
          types: place.types || [],
          photo_url: place.photos?.[0] ? placePhotoUrl(place.photos[0]) : null,
          match_reason: null,
          match_score: 0,
          sort_order: index,
          hook: place.hook || null,
          estimated_duration: place.estimatedDuration || null,
          category: place.category || null,
          distance_note: place.distanceNote || null,
          matching_vibes: vibes,
          formatted_address: place.formattedAddress || null,
          location_lat: place.location?.lat || null,
          location_lng: place.location?.lng || null,
          website_uri: place.websiteUri || null,
          opening_hours: place.currentOpeningHours ? {
            openNow: place.currentOpeningHours.openNow,
            weekdayDescriptions: place.currentOpeningHours.weekdayDescriptions
          } : null
        };
      });

      const { error: placesError } = await supabase
        .from('saved_places')
        .insert(placesToInsert);

      if (placesError) {
        setError(placesError.message);
        return null;
      }

      // H-103: verification round-trip. The previous code returned the
      // itinerary with `savedPlaces || []` — which meant a truthy return
      // value even when the SELECT came back empty due to RLS timing
      // right after OAuth. Result: the save-after-login toast fired on a
      // partial/empty trip. Fix: only treat this as a success if the
      // SELECT returns ALL the rows we just tried to insert.
      const { data: savedPlaces, error: verifyError } = await supabase
        .from('saved_places')
        .select('*')
        .eq('itinerary_id', itinerary.id)
        .order('sort_order');

      if (verifyError) {
        setError(`Save verification failed: ${verifyError.message}`);
        return null;
      }

      if (!savedPlaces || savedPlaces.length !== places.length) {
        setError(
          `Save verification mismatch: expected ${places.length} places, got ${savedPlaces?.length ?? 0}`,
        );
        return null;
      }

      // Record a save event for each place so the taste graph can learn from it
      for (const place of places) {
        recordTasteEvent(userId, place.id, 'save', {
          name: place.name,
          types: place.types,
          category: place.category,
          matchingVibes: place.matchingVibes,
        });
      }

      return {
        ...itinerary,
        places: savedPlaces,
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save itinerary';
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchItineraries = useCallback(async (userId: string, groupBy: TripGrouping = 'day'): Promise<GroupedItineraries[]> => {
    setLoading(true);
    setError(null);

    try {
      if (!supabase) {
        setError('Database not configured');
        return [];
      }
      const { data: itineraries, error: itinerariesError } = await supabase
        .from('itineraries')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (itinerariesError) {
        setError(itinerariesError.message);
        throw new Error(itinerariesError.message);
      }

      if (!itineraries || itineraries.length === 0) {
        return [];
      }

      const itineraryIds = itineraries.map(i => i.id);
      const { data: places, error: placesError } = await supabase
        .from('saved_places')
        .select('*')
        .in('itinerary_id', itineraryIds)
        .order('sort_order');

      if (placesError) {
        setError(placesError.message);
        throw new Error(placesError.message);
      }

      const placesByItinerary = (places || []).reduce((acc, place) => {
        if (!acc[place.itinerary_id]) {
          acc[place.itinerary_id] = [];
        }
        acc[place.itinerary_id].push(place);
        return acc;
      }, {} as Record<string, SavedPlace[]>);

      const itinerariesWithPlaces: SavedItinerary[] = itineraries.map(it => ({
        ...it,
        places: placesByItinerary[it.id] || []
      }));

      if (groupBy === 'location') {
        const grouped = itinerariesWithPlaces.reduce((acc, itinerary) => {
          const locationKey = normalizeLocation(itinerary.location);
          if (!acc[locationKey]) {
            acc[locationKey] = {
              groupKey: locationKey,
              groupLabel: formatLocationLabel(itinerary.location),
              itineraries: []
            };
          }
          acc[locationKey].itineraries.push(itinerary);
          return acc;
        }, {} as Record<string, GroupedItineraries>);

        return Object.values(grouped).sort((a, b) => {
          const aLatest = Math.max(...a.itineraries.map(i => new Date(i.created_at).getTime()));
          const bLatest = Math.max(...b.itineraries.map(i => new Date(i.created_at).getTime()));
          return bLatest - aLatest;
        });
      }

      const grouped = itinerariesWithPlaces.reduce((acc, itinerary) => {
        const dateKey = localDayKey(itinerary.created_at);
        if (!acc[dateKey]) {
          acc[dateKey] = {
            groupKey: dateKey,
            groupLabel: formatDateLabel(itinerary.created_at),
            itineraries: []
          };
        }
        acc[dateKey].itineraries.push(itinerary);
        return acc;
      }, {} as Record<string, GroupedItineraries>);

      return Object.values(grouped).sort((a, b) => {
        const aLatest = Math.max(...a.itineraries.map(i => new Date(i.created_at).getTime()));
        const bLatest = Math.max(...b.itineraries.map(i => new Date(i.created_at).getTime()));
        return bLatest - aLatest;
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch itineraries';
      setError(message);
      // A failed load must reach the caller; an empty list means "no trips", never "the load failed".
      throw err instanceof Error ? err : new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteItinerary = useCallback(async (itineraryId: string): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      if (!supabase) {
        setError('Database not configured');
        return false;
      }
      const { error: deleteError } = await supabase
        .from('itineraries')
        .delete()
        .eq('id', itineraryId);

      if (deleteError) {
        setError(deleteError.message);
        return false;
      }

      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete itinerary';
      setError(message);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteGroup = useCallback(async (itineraryIds: string[]): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      if (!supabase) {
        setError('Database not configured');
        return false;
      }
      const { error: deleteError } = await supabase
        .from('itineraries')
        .delete()
        .in('id', itineraryIds);

      if (deleteError) {
        setError(deleteError.message);
        return false;
      }

      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete group';
      setError(message);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const getGroupCount = useCallback(async (userId: string, groupBy: TripGrouping): Promise<number> => {
    try {
      if (!supabase) return 0;
      const { data: itineraries, error: itinerariesError } = await supabase
        .from('itineraries')
        .select('location, created_at')
        .eq('user_id', userId);

      if (itinerariesError || !itineraries) {
        return 0;
      }

      if (groupBy === 'location') {
        const uniqueLocations = new Set(itineraries.map(i => normalizeLocation(i.location)));
        return uniqueLocations.size;
      }

      const uniqueDates = new Set(itineraries.map(i => localDayKey(i.created_at)));
      return uniqueDates.size;
    } catch {
      return 0;
    }
  }, []);

  const canAddToGroup = useCallback(async (
    userId: string,
    groupBy: TripGrouping,
    newLocation: string
  ): Promise<{ allowed: boolean; currentCount: number }> => {
    try {
      if (!supabase) return { allowed: true, currentCount: 0 };
      const { data: itineraries, error: itinerariesError } = await supabase
        .from('itineraries')
        .select('location, created_at')
        .eq('user_id', userId);

      if (itinerariesError || !itineraries) {
        return { allowed: true, currentCount: 0 };
      }

      if (groupBy === 'location') {
        const uniqueLocations = new Set(itineraries.map(i => normalizeLocation(i.location)));
        const normalizedNewLocation = normalizeLocation(newLocation);

        if (uniqueLocations.has(normalizedNewLocation)) {
          return { allowed: true, currentCount: uniqueLocations.size };
        }

        return {
          allowed: uniqueLocations.size < MAX_FREE_LOCATION_GROUPS,
          currentCount: uniqueLocations.size
        };
      }

      const uniqueDates = new Set(itineraries.map(i => localDayKey(i.created_at)));
      const todayKey = localDayKey(new Date().toISOString());

      if (uniqueDates.has(todayKey)) {
        return { allowed: true, currentCount: uniqueDates.size };
      }

      return {
        allowed: uniqueDates.size < MAX_FREE_DAY_GROUPS,
        currentCount: uniqueDates.size
      };
    } catch {
      return { allowed: true, currentCount: 0 };
    }
  }, []);

  const addPlacesToItinerary = useCallback(async (
    itineraryId: string,
    places: RankedPlace[]
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      if (!supabase) {
        setError('Database not configured');
        return false;
      }
      const { data: existingPlaces } = await supabase
        .from('saved_places')
        .select('sort_order')
        .eq('itinerary_id', itineraryId)
        .order('sort_order', { ascending: false })
        .limit(1);

      const startSortOrder = existingPlaces && existingPlaces.length > 0
        ? existingPlaces[0].sort_order + 1
        : 0;

      const placesToInsert = places.map((place, index) => {
        const vibes = place.matchingVibes
          ? (Array.isArray(place.matchingVibes) ? place.matchingVibes : [place.matchingVibes])
          : [];
        return {
          itinerary_id: itineraryId,
          place_id: place.id,
          name: place.name,
          address: place.formattedAddress || null,
          rating: place.rating || null,
          user_ratings_total: place.userRatingCount || 0,
          types: place.types || [],
          photo_url: place.photos?.[0] ? placePhotoUrl(place.photos[0]) : null,
          match_reason: null,
          match_score: 0,
          sort_order: startSortOrder + index,
          hook: place.hook || null,
          estimated_duration: place.estimatedDuration || null,
          category: place.category || null,
          distance_note: place.distanceNote || null,
          matching_vibes: vibes,
          formatted_address: place.formattedAddress || null,
          location_lat: place.location?.lat || null,
          location_lng: place.location?.lng || null,
          website_uri: place.websiteUri || null,
          opening_hours: place.currentOpeningHours ? {
            openNow: place.currentOpeningHours.openNow,
            weekdayDescriptions: place.currentOpeningHours.weekdayDescriptions
          } : null
        };
      });

      const { error: placesError } = await supabase
        .from('saved_places')
        .insert(placesToInsert);

      if (placesError) {
        setError(placesError.message);
        return false;
      }

      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add places';
      setError(message);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteSavedPlace = useCallback(async (placeId: string): Promise<boolean> => {
    setError(null);
    try {
      if (!supabase) {
        setError('Database not configured');
        return false;
      }
      const { error: deleteError } = await supabase
        .from('saved_places')
        .delete()
        .eq('id', placeId);

      if (deleteError) {
        setError(deleteError.message);
        return false;
      }
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete place';
      setError(message);
      return false;
    }
  }, []);

  const deleteSavedPlaceByPlaceId = useCallback(async (itineraryId: string, placeId: string): Promise<boolean> => {
    setError(null);
    try {
      if (!supabase) {
        setError('Database not configured');
        return false;
      }
      const { error: deleteError } = await supabase
        .from('saved_places')
        .delete()
        .eq('itinerary_id', itineraryId)
        .eq('place_id', placeId);

      if (deleteError) {
        setError(deleteError.message);
        return false;
      }
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete place';
      setError(message);
      return false;
    }
  }, []);

  const updateItineraryTitle = useCallback(async (
    itineraryId: string,
    newTitle: string
  ): Promise<boolean> => {
    setError(null);

    try {
      if (!supabase) {
        setError('Database not configured');
        return false;
      }
      const { error: updateError } = await supabase
        .from('itineraries')
        .update({ title: newTitle.trim() })
        .eq('id', itineraryId);

      if (updateError) {
        setError(updateError.message);
        return false;
      }

      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update title';
      setError(message);
      return false;
    }
  }, []);

  const regenerateTripTitle = useCallback(async (
    itineraryId: string,
    location: string,
    vibes: string[],
    places: RankedPlace[],
    timeAvailable?: string,
    transport?: string
  ): Promise<boolean> => {
    const title = generateTitle(location, vibes, places, undefined, timeAvailable, transport);
    return updateItineraryTitle(itineraryId, title);
  }, [updateItineraryTitle]);

  const mergeItineraries = useCallback(async (
    targetId: string,
    sourceIds: string[],
    mergedMetadata: {
      location: string;
      time_available: string;
      transport: string;
      vibes: string[];
      ai_mode: string;
      sourceLocations?: string[];
      renamedTitles?: { title: string; placeCount: number }[];
      /** H-217: title of the pinned source (if any). When set, it wins
       *  verbatim over getMergedTitle / generateTitle. */
      pinnedTitle?: string | null;
      /** H-217: whether to mark the merge result as pinned. The unique
       *  partial index allows this only because all sources (including
       *  the previously-pinned one) get DELETED at step 6. */
      pinned?: boolean;
    },
    dedupedPlaces: SavedPlace[]
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      if (!supabase) {
        setError('Database not configured');
        return false;
      }

      // 0. Collect share slugs from ALL itineraries (target + sources) before anything is deleted
      const allIds = [targetId, ...sourceIds];
      const { data: allItins } = await supabase
        .from('itineraries')
        .select('id, share_slug, is_shared')
        .in('id', allIds);

      const sharedItins = (allItins || []).filter(i => i.share_slug && i.is_shared);
      const targetItin = sharedItins.find(i => i.id === targetId);
      const sourceSharedItins = sharedItins.filter(i => i.id !== targetId);
      const anyShared = sharedItins.length > 0;

      // If the target doesn't have a slug but sources do, adopt the first source's slug
      const targetSlug = targetItin?.share_slug || (sourceSharedItins.length > 0 ? sourceSharedItins[0].share_slug : null);
      // Any source slugs that won't become the target's primary slug need to be redirected
      const redirectSlugs = sourceSharedItins
        .map(i => i.share_slug as string)
        .filter(slug => slug !== targetSlug);
      // Also redirect the target's old slug if we're NOT keeping it (shouldn't happen, but safety)
      if (targetItin?.share_slug && targetItin.share_slug !== targetSlug) {
        redirectSlugs.push(targetItin.share_slug);
      }

      // 1. Delete existing saved_places for the target
      const { error: deleteTargetPlacesError } = await supabase
        .from('saved_places')
        .delete()
        .eq('itinerary_id', targetId);

      if (deleteTargetPlacesError) {
        setError(deleteTargetPlacesError.message);
        return false;
      }

      // 2. Insert de-duped places with new sort_order
      const placesToInsert = dedupedPlaces.map((place, index) => ({
        itinerary_id: targetId,
        place_id: place.place_id,
        name: place.name,
        address: place.address,
        rating: place.rating,
        user_ratings_total: place.user_ratings_total,
        types: place.types,
        photo_url: place.photo_url,
        match_reason: place.match_reason,
        match_score: place.match_score,
        sort_order: index,
        hook: place.hook,
        estimated_duration: place.estimated_duration,
        category: place.category,
        distance_note: place.distance_note,
        matching_vibes: place.matching_vibes,
        formatted_address: place.formatted_address,
        location_lat: place.location_lat,
        location_lng: place.location_lng,
        website_uri: place.website_uri,
        opening_hours: place.opening_hours
      }));

      const { error: insertError } = await supabase
        .from('saved_places')
        .insert(placesToInsert);

      if (insertError) {
        setError(insertError.message);
        return false;
      }

      // 3. Update target itinerary metadata (preserving share state)
      // H-217 Rule 1: pinned trip's title wins verbatim. Else fall through
      // to renamed-merge logic, else generated title.
      const title = mergedMetadata.pinnedTitle
        || getMergedTitle(mergedMetadata.renamedTitles || [])
        || generateTitle(
            mergedMetadata.location,
            mergedMetadata.vibes,
            dedupedPlaces.map(p => ({
              id: p.place_id,
              name: p.name,
              matchingVibes: p.matching_vibes
            })) as unknown as RankedPlace[],
            mergedMetadata.sourceLocations
          );

      const updatePayload: Record<string, unknown> = {
        location: mergedMetadata.location,
        time_available: mergedMetadata.time_available,
        transport: mergedMetadata.transport,
        vibes: mergedMetadata.vibes,
        ai_mode: mergedMetadata.ai_mode,
        title
      };

      // Preserve or adopt share state
      if (anyShared) {
        updatePayload.share_slug = targetSlug;
        updatePayload.is_shared = true;
      }

      // H-217 Rule 1: propagate pin to the merge target. Safe because the
      // sources (incl. the previously-pinned one) get deleted at step 6,
      // so the unique partial index never sees two pinned rows for this user.
      if (mergedMetadata.pinned) {
        updatePayload.is_pinned = true;
      }

      const { error: updateError } = await supabase
        .from('itineraries')
        .update(updatePayload)
        .eq('id', targetId);

      if (updateError) {
        setError(updateError.message);
        return false;
      }

      // 4. Insert share redirects for source slugs so old links still work
      if (redirectSlugs.length > 0) {
        const redirectRows = redirectSlugs.map(slug => ({
          slug,
          target_itinerary_id: targetId
        }));
        // Use upsert in case a redirect already exists for this slug
        await supabase
          .from('share_redirects')
          .upsert(redirectRows, { onConflict: 'slug' });
      }

      // 5. Also update any existing redirects that pointed at source itineraries
      //    to now point at the target (chain resolution)
      for (const sourceId of sourceIds) {
        await supabase
          .from('share_redirects')
          .update({ target_itinerary_id: targetId })
          .eq('target_itinerary_id', sourceId);
      }

      // 6. Delete source itineraries
      for (const sourceId of sourceIds) {
        // Clear share_slug on source before deleting (avoids unique constraint
        // issues if we adopted the slug onto the target)
        await supabase
          .from('itineraries')
          .update({ share_slug: null, is_shared: false })
          .eq('id', sourceId);

        const { error: deleteSourceError } = await supabase
          .from('saved_places')
          .delete()
          .eq('itinerary_id', sourceId);

        if (deleteSourceError) {
          setError(deleteSourceError.message);
          return false;
        }

        const { error: deleteItinError } = await supabase
          .from('itineraries')
          .delete()
          .eq('id', sourceId);

        if (deleteItinError) {
          setError(deleteItinError.message);
          return false;
        }
      }

      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to merge itineraries';
      setError(message);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const shareTrip = useCallback(async (itineraryId: string, language?: string): Promise<string | null> => {
    try {
      if (!supabase) return null;

      // Fetch itinerary to get location/time for slug
      const { data: itin } = await supabase
        .from('itineraries')
        .select('location, time_available, share_slug, is_shared')
        .eq('id', itineraryId)
        .single();

      if (!itin) return null;

      // If slug already exists, re-enable sharing if needed and return
      if (itin.share_slug) {
        const updateFields: Record<string, unknown> = {};
        if (!itin.is_shared) updateFields.is_shared = true;
        if (language) updateFields.share_language = language;
        if (Object.keys(updateFields).length > 0) {
          await supabase
            .from('itineraries')
            .update(updateFields)
            .eq('id', itineraryId);
        }
        return `https://tripboom.app/t/${itin.share_slug}`;
      }

      // Place name plus a random suffix: readable, unguessable, and nothing about the sharer.
      // Slugs issued before this change keep working because they are stored, not derived.
      const slugify = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 30);
      const randomSuffix = () => Array.from(crypto.getRandomValues(new Uint8Array(8)), b => 'abcdefghjkmnpqrstuvwxyz23456789'[b % 31]).join('');
      let slug = `${slugify(itin.location) || 'trip'}-${randomSuffix()}`;

      const { data: existing } = await supabase
        .from('itineraries')
        .select('id')
        .eq('share_slug', slug)
        .maybeSingle();

      if (existing) {
        slug = `${slugify(itin.location) || 'trip'}-${randomSuffix()}`;
      }

      const { error } = await supabase
        .from('itineraries')
        .update({ share_slug: slug, is_shared: true, ...(language ? { share_language: language } : {}) })
        .eq('id', itineraryId);

      if (error) {
        console.error('Error sharing trip:', error);
        return null;
      }

      return `https://tripboom.app/t/${slug}`;
    } catch (err) {
      console.error('Error sharing trip:', err);
      return null;
    }
  }, []);

  /**
   * H-213 v2: pin a trip. Up to 2 pins per user; if 2 are already pinned,
   * the most-recently-pinned one gets displaced. First-pinned is sticky.
   */
  const setPinnedItinerary = useCallback(async (itineraryId: string): Promise<boolean> => {
    try {
      if (!supabase) return false;
      const { data, error } = await supabase.rpc('set_pinned_itinerary', { p_id: itineraryId });
      if (error) {
        // Surface RPC errors visibly — silent swallowing hid the
        // "FOR UPDATE not allowed with aggregate" bug for too long.
        console.error('[setPinnedItinerary] RPC error:', error);
        setError(error.message);
        return false;
      }
      return Boolean(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to pin trip';
      console.error('[setPinnedItinerary] threw:', err);
      setError(message);
      return false;
    }
  }, []);

  const unpinItinerary = useCallback(async (itineraryId: string): Promise<boolean> => {
    try {
      if (!supabase) return false;
      const { data, error } = await supabase.rpc('unpin_itinerary', { p_id: itineraryId });
      if (error) {
        setError(error.message);
        return false;
      }
      return Boolean(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to unpin trip';
      setError(message);
      return false;
    }
  }, []);

  const unshareTrip = useCallback(async (itineraryId: string): Promise<boolean> => {
    try {
      if (!supabase) return false;

      const { error } = await supabase
        .from('itineraries')
        .update({ is_shared: false })
        .eq('id', itineraryId);

      if (error) {
        console.error('Error unsharing trip:', error);
        return false;
      }

      // Keep share_redirects intact — getSharedTrip() already gates on
      // is_shared=true, so redirects are harmless while unshared and
      // automatically revive if the user re-shares later.

      return true;
    } catch (err) {
      console.error('Error unsharing trip:', err);
      return false;
    }
  }, []);

  return {
    loading,
    error,
    saveItinerary,
    fetchItineraries,
    deleteItinerary,
    deleteGroup,
    getGroupCount,
    canAddToGroup,
    addPlacesToItinerary,
    deleteSavedPlace,
    deleteSavedPlaceByPlaceId,
    updateItineraryTitle,
    regenerateTripTitle,
    mergeItineraries,
    shareTrip,
    unshareTrip,
    setPinnedItinerary,
    unpinItinerary
  };
}
